import {
  Badge,
  Box,
  Card,
  CardBody,
  chakra,
  HStack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { CreditFacilityExpensePeriodInput } from "features/credit-facility/expenses/CreditFacilityExpensePeriodInput";
import {
  getCreditFacilityExpenseEventRetrieveQueryKey,
  useCreditFacilityAvailableTranchesForExpensePostingList,
  useCreditFacilityExpenseEventPeriodRetrieve,
  useCreditFacilityExpenseEventRetrieveSuspense,
  useCreditFacilityInterestExpenseCreate,
} from "gen/endpoints/credit-facility/credit-facility";
import { useCreditFacilityLoanListSuspense } from "gen/endpoints/credit-facility-loan/credit-facility-loan";
import { useTransactionCalculationRetrieve } from "gen/endpoints/transactions/transactions";
import { CreditFacilityExpenseEventUpsertDataRequest } from "gen/model/creditFacilityExpenseEventUpsertDataRequest";
import { CreditFacilityLoanData } from "gen/model/creditFacilityLoanData";
import { FundFamilyEventDataCeleryWorkflowId } from "gen/model/fundFamilyEventDataCeleryWorkflowId";
import { InterestAccrualGranularityLevelEnum } from "gen/model/interestAccrualGranularityLevelEnum";
import { parse as mathParse } from "mathjs";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Form from "shared/components/form/Form";
import { FormFieldset } from "shared/components/form/FormFieldset";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useToast } from "shared/components/toast/useToast";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { setFormValueIfChanged } from "shared/utils/FormUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export const CreditFacilityExpenseWorkflowSetup: React.FC = () => {
  const { t } = useTranslation(["creditFacility"]);
  const { currentPage } = usePageWizard();
  const { fundFamilyId, creditFacilityId, expenseEventId } =
    useRequiredStringParams();

  const { data: loans } = useCreditFacilityLoanListSuspense(
    fundFamilyId,
    creditFacilityId,
  );

  const { data: expenseEvent } = useCreditFacilityExpenseEventRetrieveSuspense(
    fundFamilyId,
    creditFacilityId,
    expenseEventId,
  );

  const [celeryWorkflowId, setCeleryWorkflowId] = useState<
    undefined | FundFamilyEventDataCeleryWorkflowId
  >(undefined);

  const invalidateQueryKeys = useInvalidateQueryKeys();

  const {
    mutateAsync: generateInterestExpenses,
    isPending: generateInterestExpenseIsLoading,
  } = useCreditFacilityInterestExpenseCreate({
    mutation: {
      onSuccess: async () =>
        invalidateQueryKeys(
          getCreditFacilityExpenseEventRetrieveQueryKey(
            fundFamilyId,
            creditFacilityId,
            expenseEventId,
          ),
        ),
    },
  });
  const form = useForm<CreditFacilityExpenseEventUpsertDataRequest>({
    defaultValues: {
      ...expenseEvent,
      loan_id: expenseEvent.loan_id || undefined,
    },
  });
  const loanId = form.watch("loan_id");
  const principalTrancheId = form.watch("principal_tranche_id");
  const interestRate = form.watch("interest_rate");

  const { data: period } = useCreditFacilityExpenseEventPeriodRetrieve(
    fundFamilyId,
    creditFacilityId,
    loanId!,
    {
      principal_tranche_id: principalTrancheId ?? undefined,
    },
    {
      query: {
        enabled: !!loanId,
      },
    },
  );

  const generateExpenses = async (
    data: CreditFacilityExpenseEventUpsertDataRequest,
  ) => {
    const event = await generateInterestExpenses({
      fundFamilyId,
      creditFacilityId,
      expenseEventId,
      data: {
        ...data,
        period_start: period?.period_start,
      },
    });
    setCeleryWorkflowId(event.celery_workflow_id);
  };
  const { showErrorToast } = useToast();

  // we update the effective date if the period service returns a suggested end date
  useEffect(() => {
    setFormValueIfChanged(
      form,
      "period_end",
      (form.formState.dirtyFields.principal_tranche_id ||
        form.formState.dirtyFields.loan_id) &&
        !form.formState.isSubmitting
        ? period?.period_end
        : expenseEvent.period_end,
    );
  }, [expenseEvent.period_end, form, period?.period_end]);

  const currentLoan = loans.find((loan) => loan.id === loanId);
  if (currentLoan && !currentLoan.interest_rate_calculation_id) {
    showErrorToast({
      message: t(
        "creditFacility:postExpense.errors.missingInterestRateCalculation",
      ),
    });
  }

  const { data: availableTranches } =
    useCreditFacilityAvailableTranchesForExpensePostingList(
      fundFamilyId,
      creditFacilityId,
      loanId || "",
      {
        query: {
          enabled:
            !!loanId &&
            currentLoan?.interest_accrual_granularity ===
              InterestAccrualGranularityLevelEnum.tranche_level,
        },
      },
    );

  const { data: transaction } = useTransactionCalculationRetrieve(
    fundFamilyId,
    currentLoan?.interest_rate_calculation_id || "",
    {
      query: {
        enabled: !!currentLoan?.interest_rate_calculation_id,
      },
    },
  );
  const calculationEvalString =
    transaction?.transaction_calculation.calculation_definitions[0].eval_string;

  const components = calculationEvalString?.split(" ");
  const term = components?.find((component) => component.includes("term"));
  const interestRateCalculation = useInterestRateCalculation({
    calculationEvalString: calculationEvalString || "",
    termVariable: term || "",
    interestRate: interestRate || "",
    currentLoan,
  });

  return (
    <PageContent>
      <PageHeader
        title={currentPage?.title || ""}
        workflowName={t("creditFacility:postExpense.workflow")}
        badge={<Badge colorScheme="warning">{t("common:draft")}</Badge>}
      />
      <PageBody>
        <Form form={form} onSubmit={generateExpenses}>
          <Card variant="form">
            <CardBody>
              <FormSelect name="loan_id" title={t("creditFacility:loans")}>
                {loans.map((loan) => (
                  <SelectOption key={loan.id} value={loan.id}>
                    {loan.name}
                  </SelectOption>
                ))}
              </FormSelect>
              {availableTranches &&
                currentLoan?.interest_accrual_granularity ==
                  InterestAccrualGranularityLevelEnum.tranche_level && (
                  <FormSelect
                    name="principal_tranche_id"
                    title={t("creditFacility:principalTranches")}
                    defaultValue={undefined}
                  >
                    {availableTranches.map((tranche) => (
                      <SelectOption key={tranche.id} value={tranche.id}>
                        {tranche.tranche_name}
                      </SelectOption>
                    ))}
                  </FormSelect>
                )}
              <CreditFacilityExpensePeriodInput
                name="period_end"
                title={t("creditFacility:postExpense.postingPeriod")}
                startDate={period?.period_start}
              />
              {!!term && (
                <FormNumberInput
                  name="interest_rate"
                  valueType="percent"
                  unsetWhenEmpty={true}
                  decimalScale={5}
                  title={t("creditFacility:postExpense.termReferenceRate")}
                />
              )}
              {interestRateCalculation && (
                <FormFieldset variant="overline" minWidth="0">
                  <HStack>
                    <chakra.legend textStyle="small">
                      {t("creditFacility:postExpense.interestRateCalculation")}
                    </chakra.legend>
                    <Tooltip
                      label={
                        <Text fontStyle="italic">{calculationEvalString}</Text>
                      }
                    >
                      <InfoCircleIcon />
                    </Tooltip>
                  </HStack>
                  <Box width="100%" p="27px">
                    <Text fontStyle="italic">{interestRateCalculation}</Text>
                  </Box>
                </FormFieldset>
              )}
            </CardBody>
          </Card>
          <PageWizardFormFooter
            isLoading={generateInterestExpenseIsLoading}
            celeryWorkflowId={celeryWorkflowId}
            // if there is no period start, there are no transactions
            canContinue={!!period?.period_start}
          />
        </Form>
      </PageBody>
    </PageContent>
  );
};

const replaceVariablesWithValues = (
  str: string,
  mapObj: Record<string, string>,
) => {
  const re = new RegExp(Object.keys(mapObj).join("|"), "gi");
  return str.replace(re, function (matched) {
    return mapObj[matched.toLowerCase()];
  });
};

const toPercentString = (value?: string | null) =>
  decimalOrZero(value).mul(100).toString();

interface UseInterestRateCalculationParams {
  termVariable: string;
  calculationEvalString: string;
  interestRate: string;
  currentLoan: CreditFacilityLoanData | undefined;
}

const useInterestRateCalculation = ({
  calculationEvalString,
  termVariable,
  interestRate,
  currentLoan,
}: UseInterestRateCalculationParams) => {
  if (!currentLoan) {
    return "";
  }
  const hasTermVariable = termVariable && interestRate;
  const evalStringWithVariables = replaceVariablesWithValues(
    calculationEvalString || "",
    {
      applicable_margin: toPercentString(currentLoan.applicable_margin),
      spread: toPercentString(currentLoan.spread),
      ...(hasTermVariable
        ? { [termVariable]: toPercentString(interestRate) }
        : {}),
    },
  );
  if (!hasTermVariable) {
    return evalStringWithVariables;
  }
  if (!evalStringWithVariables) {
    return "";
  }
  const expression = mathParse(evalStringWithVariables);
  const result = expression.evaluate();
  const formattedResult = `${decimalOrZero(result).toFixed(2).toString()}%`;
  const interestRateCalculation = calculationEvalString.trim().length
    ? `${expression.toString()} = ${formattedResult}`
    : "";

  return interestRateCalculation;
};
